// Импортируем errorLogger ПЕРВЫМ, чтобы перехватывать все ошибки с самого начала
import { errorLogger } from './errorLogger.js'; // Логирование ошибок на сервер
import './translation.js'; // Импортируем модуль перевода ВТОРЫМ, чтобы он инициализировался рано
import './audioContext.js'; // Импортируем третьим, чтобы audioContextManager был доступен
import { translate } from './translation.js'; // Импортируем функцию перевода
import { initTuner, stopAudio, setListening, isTunerActive } from './tuner.js';
import { tutorial } from './tutorial.js';

// Вспомогательные функции для нормализации частоты (как в тюнере)
function normalizeFrequencyToTarget(freq, targetFreq) {
    if (!freq || !targetFreq || freq <= 0 || targetFreq <= 0) return freq;
    
    // Нормализуем частоту к ближайшей октаве цели
    const octave = Math.round(Math.log2(freq / targetFreq));
    return freq / Math.pow(2, octave);
}

function isNoteMatch(freq, targetFreq, toleranceCents = 80) {
    if (!freq || !targetFreq || freq <= 0 || targetFreq <= 0) return false;
    
    const cents = 1200 * Math.log2(freq / targetFreq);
    return Math.abs(cents) < toleranceCents;
}

// Обработчик попаданий для режима "проиграть все" - глобальная функция для доступа из тюнера
window.handlePlayAllNoteDetection = function(result) {
    // Используем глобальные переменные
    if (!window.app || !window.app.playAllStats || !window.app.playAllStats.isTracking || !window.threeJSBand) {
        return false;
    }

    const appInstance = window.app;
    const threeJSBandInstance = window.threeJSBand;

    // Проверяем, что есть активные ноты на грифе
    if (!threeJSBandInstance.movingNotes || threeJSBandInstance.movingNotes.length === 0) {
        return false;
    }

    // Для одиночных нот ищем совпадение частоты с активными нотами на грифе
    if (!result.isChord && result.frequency) {
        // Находим все активные ноты на грифе, которые еще не были попаданы
        const activeNotes = threeJSBandInstance.movingNotes.filter(note => 
            note && 
            note.reachedFretboard && 
            !note.hit && 
            !note.missed && 
            note.noteData && 
            note.noteData.frequency
        );

        // Проверяем каждую активную ноту на совпадение частоты
        for (const note of activeNotes) {
            const expectedFrequency = note.noteData.frequency;
            if (!expectedFrequency) continue;

            // Нормализуем частоту (как в тюнере)
            const normalizedFreq = normalizeFrequencyToTarget(result.frequency, expectedFrequency);
            
            // Проверяем совпадение (в пределах 80 центов, как в тюнере)
            if (isNoteMatch(normalizedFreq, expectedFrequency)) {
                // Отмечаем попадание напрямую
                const hit = threeJSBandInstance.markNoteAsHit(note.string, note.fret);
                if (hit) {
                    // Создаем уникальный идентификатор ноты
                    const noteId = `${note.string}-${note.fret}-${note.originalTargetTime || note.targetTime || note.sequenceIndex || 0}`;
                    if (!appInstance.playAllStats.trackedNotes.has(noteId)) {
                        appInstance.playAllStats.trackedNotes.add(noteId);
                        appInstance.playAllStats.correctCount = appInstance.playAllStats.trackedNotes.size;
                        // Запускаем анимацию "удара сердца" на кардиографе
                        triggerHeartbeatJump();
                    }
                    return true;
                }
            }
        }
    }

    // Для аккордов проверяем все ноты аккорда
    if (result.isChord && result.playedFrequencies && result.playedFrequencies.length > 0) {
        const activeNotes = threeJSBandInstance.movingNotes.filter(note => 
            note && 
            note.reachedFretboard && 
            !note.hit && 
            !note.missed && 
            note.noteData && 
            note.noteData.frequency
        );

        // Группируем ноты по времени (аккорды)
        const noteGroups = new Map();
        activeNotes.forEach(note => {
            const timeKey = note.originalTargetTime || note.targetTime || 0;
            if (!noteGroups.has(timeKey)) {
                noteGroups.set(timeKey, []);
            }
            noteGroups.get(timeKey).push(note);
        });

        // Проверяем каждую группу нот (аккорд)
        for (const [timeKey, notes] of noteGroups) {
            let matchedCount = 0;
            const notesToHit = [];

            for (const note of notes) {
                const expectedFrequency = note.noteData.frequency;
                if (!expectedFrequency) continue;

                // Проверяем, есть ли совпадение частоты среди сыгранных
                const matched = result.playedFrequencies.some(playedFreq => {
                    const normalizedFreq = normalizeFrequencyToTarget(playedFreq, expectedFrequency);
                    return isNoteMatch(normalizedFreq, expectedFrequency);
                });

                if (matched) {
                    matchedCount++;
                    notesToHit.push(note);
                }
            }

            // Если большая часть нот аккорда совпала (>= 60%), считаем попадание
            if (matchedCount > 0 && matchedCount >= Math.max(1, Math.round(notes.length * 0.6))) {
                // Отмечаем все совпавшие ноты как попадания
                let anyNewHit = false;
                notesToHit.forEach(note => {
                    const hit = threeJSBandInstance.markNoteAsHit(note.string, note.fret);
                    if (hit) {
                        const noteId = `${note.string}-${note.fret}-${note.originalTargetTime || note.targetTime || note.sequenceIndex || 0}`;
                        if (!appInstance.playAllStats.trackedNotes.has(noteId)) {
                            appInstance.playAllStats.trackedNotes.add(noteId);
                            appInstance.playAllStats.correctCount = appInstance.playAllStats.trackedNotes.size;
                            anyNewHit = true;
                        }
                    }
                });
                // Запускаем анимацию "удара сердца" на кардиографе при попадании
                if (anyNewHit) {
                    triggerHeartbeatJump();
                }
                return true;
            }
        }
    }

    return false;
};

import { initSettings } from './settings.js';
import { initNotes, getSongData, getFullMelodySequence, getMeasuresForBlock, getCurrentTempo, getCurrentNote, nextNote, setCurrentNoteIndex, getCurrentNoteIndex, getMelodyBlocks } from './notes.js';
import { initGame, resetGameProgress, setupBlockClickHandlers, stopGame, getGameStars, triggerHeartbeatJump, updateGameUI } from './game.js';
import './utils.js';
import { playMelody, setUrl, stopPlayback, loadMusicWithProgress, getIsPlaying } from './player.js';
import { stopBlockPlayback } from './blockPlayer.js';
import { initConfig } from "./userconfig.js";
import { startCountdown } from './utils/countdown.js';
import * as Tone from 'tone';
import AuthUI from './authUI.js';
import auth from './auth.js';
import { getUserConfig } from './userconfig.js';
import { fetchSongData } from './api.js';
import { threeJSBand } from './threejsBand.js';
// Делаем threeJSBand доступным глобально для обработчика попаданий
window.threeJSBand = threeJSBand;

// Функции для получения путей к аудио файлам
const getSongFullAudioPath = (songId) => `/audio/songs/${songId}/full.mp3`;
const getSongBackgroundAudioPath = (songId) => `/audio/songs/${songId}/background.mp3`;

// Подсчет общего количества нот в последовательности (без пауз)
function countTotalNotesInSequence() {
    const fullSequence = getFullMelodySequence();
    if (!fullSequence || fullSequence.length === 0) {
        return 0;
    }

    let totalNotes = 0;
    fullSequence.forEach(block => {
        if (!block.isPause && block.id) {
            const blockMeasures = getMeasuresForBlock(block.id);
            if (blockMeasures) {
                blockMeasures.forEach(measure => {
                    measure.forEach(chordGroup => {
                        const isPause = chordGroup[0] && chordGroup[0].pause === "true";
                        if (!isPause) {
                            chordGroup.forEach(noteData => {
                                if (noteData && noteData.noteName) {
                                    totalNotes++;
                                }
                            });
                        }
                    });
                });
            }
        }
    });

    return totalNotes;
}

// Показать сообщение с результатами точности для режима "проиграть все"
function showPlayAllResultMessage(accuracy, correctCount, totalCount) {
    return new Promise((resolve) => {
        const messageContainer = document.createElement('div');
        messageContainer.className = 'play-all-result-overlay';
        messageContainer.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.9);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 10000;
            font-size: 48px;
            color: white;
            font-weight: bold;
            text-align: center;
            flex-direction: column;
        `;

        const resultHtml = `
            <div style="font-size: 64px; margin-bottom: 30px;">${translate('Песня завершена!')}</div>
            <div style="font-size: 56px; margin-bottom: 20px;">${translate('Точность:')} ${accuracy}%</div>
            <div style="font-size: 36px; margin-top: 20px;">${translate('Попаданий:')} ${correctCount}/${totalCount}</div>
            <button id="closeResultBtn" style="
                margin-top: 40px;
                padding: 15px 40px;
                font-size: 24px;
                cursor: pointer;
                background: #4CAF50;
                color: white;
                border: none;
                border-radius: 5px;
                font-weight: bold;
            ">${translate('Закрыть')}</button>
        `;

        messageContainer.innerHTML = resultHtml;
        document.body.appendChild(messageContainer);

        const closeButton = document.getElementById('closeResultBtn');
        closeButton.addEventListener('click', () => {
            if (messageContainer.parentNode) {
                messageContainer.parentNode.removeChild(messageContainer);
            }
            resolve();
        });

        // Также закрываем по клику вне кнопки (на overlay)
        messageContainer.addEventListener('click', (e) => {
            if (e.target === messageContainer) {
                if (messageContainer.parentNode) {
                    messageContainer.parentNode.removeChild(messageContainer);
                }
                resolve();
            }
        });
    });
}

// Функция для подготовки и отображения нот первого блока во время отсчета
function prepareFirstBlockNotesForCountdown() {
    if (!threeJSBand) {
        return;
    }

    const fullSequence = getFullMelodySequence();
    if (!fullSequence || fullSequence.length === 0) {
        return;
    }

    // Находим первый блок (пропускаем паузы)
    const firstBlock = fullSequence.find(block => !block.isPause);
    if (!firstBlock || !firstBlock.id) {
        return;
    }

    const blockMeasures = getMeasuresForBlock(firstBlock.id);
    if (!blockMeasures || blockMeasures.length === 0) {
        return;
    }

    const allBlockNotes = [];
    blockMeasures.forEach(measure => {
        measure.forEach(chordGroup => {
            const isPause = chordGroup[0] && chordGroup[0].pause === "true";
            if (!isPause) {
                chordGroup.forEach(noteData => {
                    if (noteData && noteData.noteName) {
                        allBlockNotes.push(noteData);
                    }
                });
            }
        });
    });

    threeJSBand.clearAllMovingNotes();

    if (allBlockNotes.length > 0) {
        threeJSBand.animateCameraToBlock(allBlockNotes);
    }

    const currentTime = Tone.now();
    const tempo = getCurrentTempo() || 120;
    // Используем стандартную скорость для отображения (не игровой режим)
    const speedFactor = 1;

    // Подготавливаем ноты с правильными позициями (вдали от грифа) без движения
    
    // Минимальное время полета для каждой ноты
    const MIN_NOTE_TRAVEL_TIME = 1.5;
    
    let timeCursor = 0;
    
    blockMeasures.forEach(measure => {
        measure.forEach(chordGroup => {
            const isPause = chordGroup[0] && chordGroup[0].pause === "true";
            if (!isPause) {
                const durationValue = chordGroup[0].duration;
                const isDotted = chordGroup[0].dotted;

                const stepDuration = 4 / durationValue;
                const actualDuration = isDotted ? stepDuration * 1.5 : stepDuration;

                // Время прилета к грифу С учетом speedFactor (здесь = 1)
                // speedFactor применяется ко ВСЕМУ timeCursor, поэтому интервалы пропорциональны
                let groupTargetTime = currentTime + (timeCursor * (60 / tempo) / speedFactor);
                
                // НЕ корректируем первую ноту! initialZ уже правильно рассчитан
                
                // Каждая нота должна лететь MIN_NOTE_TRAVEL_TIME
                // Рассчитываем startTime для этой группы нот
                const groupNoteStartTime = groupTargetTime - MIN_NOTE_TRAVEL_TIME;

                chordGroup.forEach(noteData => {
                    // Добавляем ноты с правильными позициями
                    // Каждая группа нот имеет свой startTime, рассчитанный из targetTime
                    const note = threeJSBand.addMovingNote(
                        noteData,
                        groupNoteStartTime, // Время создания = targetTime - MIN_NOTE_TRAVEL_TIME
                        groupTargetTime, // targetTime - момент достижения грифа
                        false, // activated = false для countdown (используем actualStartTime для initialZ)
                        null
                    );
                    if (note) {
                        // Помечаем ноты как ожидающие, чтобы они не обновлялись
                        note.isWaiting = true;
                        // Устанавливаем targetTime в MAX_SAFE_INTEGER чтобы движение не началось
                        note.targetTime = Number.MAX_SAFE_INTEGER;
                        note.progress = 0;
                        // Сохраняем groupTargetTime для группировки первой группы нот
                        note._staticGroupTime = groupTargetTime;
                    }
                });

                timeCursor += actualDuration;
            } else {
                const pauseDuration = chordGroup[0].duration;
                const isDotted = chordGroup[0].dotted || false;

                const stepDuration = 4 / pauseDuration;
                const actualDuration = isDotted ? stepDuration * 1.5 : stepDuration;
                timeCursor += actualDuration;
            }
        });
    });

    // После добавления всех нот обновляем hammer эффекты
    if (threeJSBand) {
        threeJSBand.updateHammerEffects();
        
        // Создаем маркеры пальцев для первой группы нот (первой по времени)
        const allStaticNotes = threeJSBand.movingNotes.filter(n => n._staticGroupTime !== undefined);
        if (allStaticNotes.length > 0) {
            const firstGroupTime = Math.min(...allStaticNotes.map(n => n._staticGroupTime));
            const firstGroupNotes = allStaticNotes.filter(n => 
                n._staticGroupTime === firstGroupTime &&
                !n.fingerSphereCreated &&
                n.noteData &&
                n.noteData.finger !== undefined &&
                n.noteData.finger !== null
            );
            
            // Создаем маркеры для первой группы нот
            firstGroupNotes.forEach(note => {
                const position = threeJSBand.fretStringPositions[note.fret];
                if (position && position[note.string - 1]) {
                    const pos = position[note.string - 1];
                    const fingerCircle = threeJSBand.createFingerCircle(
                        note.noteData.finger,
                        pos.x,
                        pos.y - 8,
                        note.string
                    );
                    if (fingerCircle) {
                        fingerCircle.visible = true;
                        threeJSBand.scene.add(fingerCircle);
                        note.fingerSphere = fingerCircle;
                        note.fingerSphereCreated = true;
                        threeJSBand.fingerSpheres.push({
                            sphere: fingerCircle,
                            string: note.string,
                            fret: note.fret,
                            finger: note.noteData.finger
                        });
                    }
                }
            });
        }
    }
}

// Константы для CSS классов и селекторов
const SELECTORS = {
    GUITAR_SECTION: '.guitar-section',
    SETTINGS_SECTION: '.settings-section',
    ACTIVE_TAB: '.tab.active'
};

// Константы для подтверждений (переводятся динамически)
const CONFIRM_MESSAGES = {
    RESET_PROGRESS: 'Вы уверены, что хотите сбросить весь прогресс? Это действие нельзя отменить.'
};

// Функция для получения переведенного сообщения
function getConfirmMessage(key) {
    const message = CONFIRM_MESSAGES[key];
    return window.translator ? window.translator.translateText(message) : message;
}

class App {
    constructor() {
        this.elements = this.getDOMElements();
        this.isInitialized = false;
        this.listenersSetup = false; // Флаг для предотвращения дублирования обработчиков
        this.currentTabMode = 'guitar'; // Текущий режим: 'guitar' или 'settings'
        // Счетчики для режима "проиграть все"
        this.playAllStats = {
            totalNotes: 0,
            correctCount: 0,
            isTracking: false,
            trackingInterval: null,
            trackedNotes: new Set() // Множество уникальных идентификаторов сыгранных нот (string-fret-time)
        };
        // Флаг для предотвращения множественных одновременных вызовов handleStopPlay
        this.isStopping = false;
        this.stopPlayPromise = null;
        // Флаг для отслеживания выполнения handleStartPlayMusic
        this.isStartingPlayMusic = false;
        this.startPlayMusicAbortController = null;
        // Флаг для отслеживания выполнения handlePlayAll
        this.isPlayingPlayAll = false;
    }

    getDOMElements() {
        return {
            micBtn: document.getElementById('micBtn'),
            stopBtn: document.getElementById('stopBtn'),
            tabs: document.querySelectorAll('.tab'),
            toggleTabBtn: document.getElementById('toggleTabBtn'),
            stopPlay: document.getElementById('stopPlay'),
            resetProgressBtn: document.getElementById('resetProgressBtn'),
            restartTutorialBtn: document.getElementById('restartTutorialBtn'),
            playAllBtn: document.getElementById('playAllBtn'),
            startPlayMusic: document.getElementById('startPlayMusic'),
        };
    }

    // Проверка мобильного устройства
    checkMobileDevice() {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
        
        if (isMobile) {
            const message = translate('Рекомендуем использовать ПК или ноутбук для комфортной игры.');
            
            // Создаем модальное окно с кнопкой закрытия
            const modal = document.createElement('div');
            modal.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.8);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 10000;
            `;
            
            const content = document.createElement('div');
            content.style.cssText = `
                background: white;
                padding: 30px;
                border-radius: 10px;
                max-width: 400px;
                text-align: center;
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
            `;
            
            const messageDiv = document.createElement('div');
            messageDiv.textContent = message;
            messageDiv.style.cssText = `
                font-size: 18px;
                margin-bottom: 20px;
                color: #333;
            `;
            
            const closeButton = document.createElement('button');
            closeButton.textContent = translate('Понятно');
            closeButton.style.cssText = `
                padding: 10px 30px;
                font-size: 16px;
                background: #4CAF50;
                color: white;
                border: none;
                border-radius: 5px;
                cursor: pointer;
                font-weight: bold;
            `;
            
            const closeModal = () => {
                if (modal.parentNode) {
                    modal.parentNode.removeChild(modal);
                }
            };
            
            closeButton.addEventListener('click', closeModal);
            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    closeModal();
                }
            });
            
            content.appendChild(messageDiv);
            content.appendChild(closeButton);
            modal.appendChild(content);
            document.body.appendChild(modal);
        }
    }

    async init() {
        if (this.isInitialized) return;

        try {
            // Проверяем мобильное устройство
            this.checkMobileDevice();
            
            // Инициализируем UI авторизации (проверяет авторизацию и показывает форму если нужно)
            const authUI = new AuthUI();
            
            // Проверяем авторизацию перед инициализацией приложения
            const mainContainer = document.getElementById('mainContainer');
            if (!mainContainer || mainContainer.style.display === 'none') {
                // Пользователь не авторизован, UI авторизации покажет форму
                // Приложение будет инициализировано после успешной авторизации
                return;
            }

            await this.initializeDependencies();
            this.setupEventListeners();
            this.setupAdminVisibility();
            this.showActiveTab();
            this.updateTabIcon();
            this.updateUserAndSongInfo();
            this.isInitialized = true;
            
            // Если в Telegram - разворачиваем на полный экран
            const isTelegram = typeof window.Telegram !== 'undefined' && window.Telegram.WebApp;
            if (isTelegram) {
                setTimeout(() => {
                    this.expandTelegramApp();
                }, 100);
            } else {
                // Если не в Telegram - обновляем масштабирование для мобильных
                if (typeof setupMobileScaling === 'function') {
                    setTimeout(() => setupMobileScaling(), 100);
                }
            }
            
            // Обновляем состояние кнопки микрофона (блокируем на мобильных без наушников)
            setTimeout(() => {
                this.updateMicrophoneButtonState();
            }, 500);
            
            // Слушаем изменения аудиоустройств для обновления состояния кнопки
            if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
                navigator.mediaDevices.addEventListener('devicechange', () => {
                    setTimeout(() => {
                        this.updateMicrophoneButtonState();
                    }, 300);
                });
            }
            
            // Запускаем tutorial если нужно
            if (tutorial.shouldShowTutorial()) {
                // Небольшая задержка для завершения рендеринга UI
                setTimeout(() => {
                    tutorial.start();
                }, 500);
            }
        } catch (error) {
            this.handleError(translate('Ошибка инициализации приложения:'), error);
        }
    }
    
    // Разворачивает приложение на весь экран в Telegram Mini App
    expandTelegramApp() {
        // ОТКЛЮЧЕНО: автоматическое разворачивание на весь экран
        // Раскомментируйте код ниже, если хотите вернуть автоматическое разворачивание
        
        /*
        // Проверяем, что мы находимся в Telegram WebApp
        if (typeof window.Telegram !== 'undefined' && window.Telegram.WebApp) {
            const tg = window.Telegram.WebApp;
            
            // Используем expand() для Telegram Mini App
            if (tg.expand && typeof tg.expand === 'function') {
                try {
                    tg.expand();
                    console.log('Telegram Mini App expanded');
                } catch (error) {
                    // Подавляем ошибку, если Telegram API не поддерживает метод
                    if (error.message && !error.message.includes('not supported')) {
                        console.warn('Failed to expand Telegram Mini App:', error);
                    }
                }
            }
        }
        */
        
        // Применяем масштабирование для мобильных устройств в Telegram
        if (typeof window.Telegram !== 'undefined' && window.Telegram.WebApp) {
            this.applyTelegramScaling();
        }
    }
    
    // Блокирует ориентацию экрана в альбомную (landscape) для мобильных устройств
    lockScreenOrientation() {
        // Проверяем, что это мобильное устройство
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
        
        if (!isMobile) {
            return; // Не мобильное устройство, не блокируем ориентацию
        }
        
        // Используем Screen Orientation API, если доступен
        if (screen.orientation && screen.orientation.lock) {
            screen.orientation.lock('landscape').then(() => {
                console.log('Экран заблокирован в альбомной ориентации');
            }).catch((error) => {
                console.warn('Не удалось заблокировать ориентацию экрана:', error);
                // Если не удалось заблокировать, пробуем альтернативные методы
                this.tryAlternativeOrientationLock();
            });
        } else if (screen.lockOrientation) {
            // Устаревший API (для старых браузеров)
            try {
                screen.lockOrientation('landscape');
                console.log('Экран заблокирован в альбомной ориентации (legacy API)');
            } catch (error) {
                console.warn('Не удалось заблокировать ориентацию экрана (legacy):', error);
                this.tryAlternativeOrientationLock();
            }
        } else if (screen.mozLockOrientation) {
            // Firefox
            try {
                screen.mozLockOrientation('landscape');
                console.log('Экран заблокирован в альбомной ориентации (Firefox)');
            } catch (error) {
                console.warn('Не удалось заблокировать ориентацию экрана (Firefox):', error);
                this.tryAlternativeOrientationLock();
            }
        } else if (screen.msLockOrientation) {
            // IE/Edge
            try {
                screen.msLockOrientation('landscape');
                console.log('Экран заблокирован в альбомной ориентации (IE/Edge)');
            } catch (error) {
                console.warn('Не удалось заблокировать ориентацию экрана (IE/Edge):', error);
                this.tryAlternativeOrientationLock();
            }
        } else {
            // API не поддерживается, пробуем альтернативные методы
            this.tryAlternativeOrientationLock();
        }
    }
    
    // Альтернативный метод блокировки ориентации через CSS и предупреждение
    tryAlternativeOrientationLock() {
        // Добавляем CSS класс для landscape ориентации
        document.body.classList.add('force-landscape');
        
        // Показываем сообщение пользователю о необходимости повернуть устройство
        const orientationMessage = document.createElement('div');
        orientationMessage.id = 'orientation-message';
        orientationMessage.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.9);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            color: white;
            font-size: 24px;
            text-align: center;
            padding: 20px;
        `;
        orientationMessage.innerHTML = `
            <div style="font-size: 48px; margin-bottom: 20px;">📱</div>
            <div>Пожалуйста, поверните устройство в альбомную ориентацию</div>
        `;
        
        // Проверяем ориентацию и скрываем сообщение, если устройство уже в landscape
        const checkOrientation = () => {
            const isLandscape = window.innerWidth > window.innerHeight;
            if (isLandscape) {
                orientationMessage.style.display = 'none';
            } else {
                orientationMessage.style.display = 'flex';
            }
        };
        
        checkOrientation();
        window.addEventListener('orientationchange', checkOrientation);
        window.addEventListener('resize', checkOrientation);
        
        document.body.appendChild(orientationMessage);
        
        // Удаляем сообщение через 5 секунд, если пользователь не повернул устройство
        setTimeout(() => {
            if (orientationMessage.parentNode) {
                orientationMessage.remove();
            }
        }, 5000);
    }
    
    // Применяет масштабирование для Telegram на мобильных устройствах
    applyTelegramScaling() {
        const container = document.getElementById('mainContainer');
        if (!container) {
            // Если контейнер еще не создан, попробуем позже
            setTimeout(() => this.applyTelegramScaling(), 100);
            return;
        }
        
        // Проверяем, что это мобильное устройство
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
        
        if (!isMobile) {
            // Для десктопов убираем масштабирование
            container.style.transform = '';
            container.style.width = '';
            container.style.height = '';
            this.updateTelegramModalScaling(1);
            return;
        }
        
        // Базовая ширина контента (такая же, как в setupMobileScaling)
        const baseWidth = 1200;
        const baseHeight = 800;
        
        const updateScale = () => {
            // Проверяем, виден ли контейнер
            if (container.style.display === 'none') {
                return;
            }
            
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            
            // Рассчитываем масштаб на основе ширины экрана (вписываем по ширине)
            const scale = viewportWidth / baseWidth;
            
            container.style.transform = `scale(${scale})`;
            container.style.transformOrigin = 'top left';
            container.style.width = `${baseWidth}px`;
            container.style.height = `${baseHeight}px`;
            
            // Применяем тот же масштаб к модальным окнам
            this.updateTelegramModalScaling(scale);
        };
        
        // Вызываем сразу и после небольшой задержки
        updateScale();
        setTimeout(updateScale, 100);
        setTimeout(updateScale, 500);
        
        // Удаляем старый обработчик, если он был
        if (this.telegramScaleHandler) {
            window.removeEventListener('resize', this.telegramScaleHandler);
            window.removeEventListener('orientationchange', this.telegramScaleHandler);
        }
        
        // Сохраняем ссылку на обработчик
        this.telegramScaleHandler = updateScale;
        window.addEventListener('resize', this.telegramScaleHandler);
        window.addEventListener('orientationchange', () => {
            setTimeout(this.telegramScaleHandler, 100);
        });
        
        // Наблюдаем за изменением видимости контейнера
        if (this.telegramScaleObserver) {
            this.telegramScaleObserver.disconnect();
        }
        
        this.telegramScaleObserver = new MutationObserver(() => {
            updateScale();
        });
        
        this.telegramScaleObserver.observe(container, {
            attributes: true,
            attributeFilter: ['style']
        });
        
        // Наблюдаем за появлением модальных окон и применяем масштабирование
        if (this.telegramModalObserver) {
            this.telegramModalObserver.disconnect();
        }
        
        this.telegramModalObserver = new MutationObserver(() => {
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const baseWidth = 1200;
            const scale = viewportWidth / baseWidth;
            this.updateTelegramModalScaling(scale);
        });
        
        this.telegramModalObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
    
    // Обновляет масштабирование модальных окон для Telegram
    updateTelegramModalScaling(scale) {
        // Применяем масштаб к контенту модальных окон (overlay элементы не масштабируем)
        const modalSelectors = [
            '.auth-container',
            '.modal-content'
        ];
        
        modalSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                if (element) {
                    element.style.transform = `scale(${scale})`;
                    element.style.transformOrigin = 'center';
                }
            });
        });
        
        // Масштабируем контент внутри loading-overlay
        const loadingContentSelectors = [
            '.loading-message',
            '.loading-progress',
            '.loading-progress-text'
        ];
        
        loadingContentSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                if (element) {
                    element.style.transform = `scale(${scale})`;
                    element.style.transformOrigin = 'center';
                }
            });
        });
        
        // Масштабируем контент внутри игровых сообщений (overlay элементы не масштабируем)
        const gameMessageContentSelectors = [
            '.game-message-content'
        ];
        
        gameMessageContentSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                if (element) {
                    element.style.transform = `scale(${scale})`;
                    element.style.transformOrigin = 'center';
                }
            });
        });
        
        // Для tutorial-popup комбинируем масштабирование с центрированием
        const tutorialPopups = document.querySelectorAll('.tutorial-popup');
        tutorialPopups.forEach(element => {
            if (element) {
                element.style.transform = `translate(-50%, -50%) scale(${scale})`;
                element.style.transformOrigin = 'center';
            }
        });
    }

    async reinitAfterLogin() {
        try {
            // Сбрасываем флаг инициализации для переинициализации
            this.isInitialized = false;
            
            // Переинициализируем зависимости (загрузит данные нового пользователя)
            // userConfig.init() автоматически обновит userId из auth.isLoggedIn()
            await this.initializeDependencies();
            
            // setupEventListeners можно вызывать безопасно - addEventListener не создает дубликаты
            this.setupEventListeners();
            
            // Обновляем видимость админских элементов
            this.setupAdminVisibility();
            
            // Показываем активную вкладку
            this.showActiveTab();
            
            // Обновляем информацию о пользователе и песне
            await this.updateUserAndSongInfo();
            
            this.isInitialized = true;
            
            // Обновляем состояние кнопки микрофона (блокируем на мобильных без наушников)
            setTimeout(() => {
                this.updateMicrophoneButtonState();
            }, 500);
            
            // Слушаем изменения аудиоустройств для обновления состояния кнопки
            if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
                navigator.mediaDevices.addEventListener('devicechange', () => {
                    setTimeout(() => {
                        this.updateMicrophoneButtonState();
                    }, 300);
                });
            }
            
            // Если в Telegram - разворачиваем на полный экран
            const isTelegram = typeof window.Telegram !== 'undefined' && window.Telegram.WebApp;
            if (isTelegram) {
                setTimeout(() => {
                    this.expandTelegramApp();
                }, 100);
            } else {
                // Если не в Telegram - обновляем масштабирование для мобильных
                if (typeof setupMobileScaling === 'function') {
                    setTimeout(() => setupMobileScaling(), 100);
                }
            }
            
            // Запускаем tutorial если нужно (после входа)
            if (tutorial.shouldShowTutorial()) {
                // Небольшая задержка для завершения рендеринга UI
                setTimeout(() => {
                    tutorial.start();
                }, 500);
            }
        } catch (error) {
            this.handleError(translate('Ошибка переинициализации приложения после входа:'), error);
        }
    }

    async initializeDependencies() {
        await initConfig();
        await initSettings();
        await initNotes();
        setupBlockClickHandlers();
    }

    /**
     * Обновляет информацию о пользователе и песне в верхней панели
     * Название песни получается из таблицы songs поле name через API
     */
    async updateUserAndSongInfo() {
        try {
            // Получаем имя пользователя
            const userName = auth.getUsername();
            const userNameEl = document.getElementById('userName');
            if (userNameEl && userName) {
                userNameEl.textContent = userName;
                
                // Делаем имя пользователя кликабельным
                userNameEl.style.cursor = 'pointer';
                
                // Удаляем предыдущий обработчик если есть
                const oldHandler = userNameEl._clickHandler;
                if (oldHandler) {
                    userNameEl.removeEventListener('click', oldHandler);
                }
                
                // Добавляем новый обработчик
                const clickHandler = () => this.handleUserNameClick();
                userNameEl.addEventListener('click', clickHandler);
                userNameEl._clickHandler = clickHandler;
            }
            
            // Показываем/скрываем кнопку logout в зависимости от типа пользователя и устройства
            const logoutBtn = document.getElementById('logoutBtn');
            if (logoutBtn) {
                const isGuest = auth.isGuestUser();
                const isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                
                console.log('🔐 Обновление кнопки logout:', { isGuest, isMobile, willShow: !isGuest && !isMobile });
                
                // Показываем только если: НЕ гость И НЕ мобильное устройство
                if (!isGuest && !isMobile) {
                    logoutBtn.style.display = 'inline-flex';
                } else {
                    logoutBtn.style.display = 'none';
                }
            }

            
            // Получаем название песни из БД (таблица songs, поле name)
            const userConfig = await getUserConfig();
            const userProgress = userConfig.userProgress || [];
            const songId = userProgress[0]?.song_id || 1;
            
            // Получаем данные песни из БД через API /api/song_data/:id
            // API возвращает объект с полями из таблицы songs, включая поле name
            const songData = await fetchSongData(songId);
            
            const songNameEl = document.getElementById('songName');
            if (songNameEl && songData && songData.name) {
                // Отображаем название песни из поля name таблицы songs
                songNameEl.textContent = songData.name;
            } else if (songNameEl) {
                songNameEl.textContent = translate('Песня не выбрана');
            }
        } catch (error) {
            console.error('Ошибка обновления информации о пользователе и песне:', error);
        }
    }

    setupAdminVisibility() {
        // Показываем/скрываем расширенные настройки эффектов только для администратора
        const effectsControls = document.querySelector('.effects-controls');
        if (effectsControls) {
            if (auth.isAdmin()) {
                effectsControls.style.display = 'block';
            } else {
                effectsControls.style.display = 'none';
            }
        }
    }

    // Вспомогательная функция для обертки обработчиков с вызовом handleStopPlay
    async wrapWithStopPlay(handler, skipStop = false) {
        if (!skipStop) {
            await this.handleStopPlay();
        }
        const result = handler();
        // Если handler возвращает Promise, ждем его выполнения
        if (result instanceof Promise) {
            return await result;
        }
        return result;
    }

    setupEventListeners() {
        // Проверяем, не установлены ли уже обработчики (избегаем дублирования при reinitAfterLogin)
        if (this.listenersSetup) {
            return;
        }
        
        const { micBtn, stopBtn, tabs, toggleTabBtn, stopPlay, resetProgressBtn, restartTutorialBtn, playAllBtn, startPlayMusic } = this.elements;

        micBtn.addEventListener('click', () => this.wrapWithStopPlay(() => this.handleMicClick()));
        stopBtn.addEventListener('click', () => this.wrapWithStopPlay(() => stopAudio(), true)); // skipStop для самой кнопки stop

        // Обработчик для кнопки переключения режимов
        if (toggleTabBtn) {
            toggleTabBtn.addEventListener('click', () => this.wrapWithStopPlay(() => this.toggleTab()));
        }

        // Старые обработчики для совместимости (если есть другие кнопки)
        tabs.forEach(tab => {
            if (tab !== toggleTabBtn) {
                tab.addEventListener('click', () => this.wrapWithStopPlay(() => this.switchTab(tab.dataset.tab)));
            }
        });

        stopPlay.addEventListener('click', () => this.handleStopPlay()); // Сама кнопка stop не оборачивается
        resetProgressBtn.addEventListener('click', () => this.wrapWithStopPlay(() => this.handleResetProgress()));
        
        // Обработчик для перезапуска tutorial
        if (restartTutorialBtn) {
            restartTutorialBtn.addEventListener('click', () => this.wrapWithStopPlay(() => {
                // Переключаемся на вкладку с игрой перед запуском tutorial
                this.switchTab('guitar');
                tutorial.reset();
                tutorial.start();
            }));
        }

        playAllBtn.addEventListener('click', () => this.wrapWithStopPlay(() => this.handlePlayAll()));
        // НЕ оборачиваем в wrapWithStopPlay, т.к. handleStartPlayMusic сам управляет остановкой/запуском
        startPlayMusic.addEventListener('click', () => this.handleStartPlayMusic());
        
        // Устанавливаем флаг что обработчики установлены
        this.listenersSetup = true;
    }

    async handleMicClick() {
        // Проверяем, можно ли использовать микрофон на мобильном устройстве
        const canUseMicrophone = await this.checkMicrophoneAvailability();
        if (!canUseMicrophone) {
            const micBtn = document.getElementById('micBtn');
            if (micBtn) {
                const originalText = micBtn.textContent;
                micBtn.textContent = translate('Подключите наушники для использования микрофона');
                micBtn.disabled = true;
                setTimeout(() => {
                    micBtn.textContent = originalText;
                    micBtn.disabled = false;
                }, 3000);
            }
            return;
        }
        
        const toneReady = await this.initializeTone();
        if (toneReady) {
            initTuner();
        }
    }
    
    // Проверяет, можно ли использовать микрофон (на мобильных только с наушниками)
    async checkMicrophoneAvailability() {
        // Проверяем, что это мобильное устройство
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
        
        // Если не мобильное устройство, микрофон всегда доступен
        if (!isMobile) {
            return true;
        }
        
        // На мобильных устройствах проверяем наличие наушников
        return await this.checkHeadphonesConnected();
    }
    
    // Проверяет, подключены ли внешние наушники (не встроенные динамики)
    async checkHeadphonesConnected() {
        try {
            // Проверяем поддержку MediaDevices API
            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                // Если API не поддерживается, блокируем микрофон на мобильных (безопаснее)
                return false;
            }
            
            // Пытаемся получить список устройств
            let devices;
            try {
                devices = await navigator.mediaDevices.enumerateDevices();
            } catch (error) {
                console.warn('Не удалось получить список устройств:', error);
                // Если не удалось получить список, блокируем микрофон на мобильных
                return false;
            }
            
            const audioOutputs = devices.filter(device => device.kind === 'audiooutput');
            
            // Если нет устройств вывода, блокируем микрофон (безопаснее)
            if (audioOutputs.length === 0) {
                return false;
            }
            
            // Проверяем, есть ли названия устройств (они могут быть недоступны без разрешения)
            const hasDeviceLabels = audioOutputs.some(device => device.label && device.label.trim() !== '');
            
            if (!hasDeviceLabels) {
                // Если названия недоступны, пытаемся запросить разрешение для получения полного списка
                try {
                    const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    tempStream.getTracks().forEach(track => track.stop());
                    // Повторно получаем список после разрешения
                    devices = await navigator.mediaDevices.enumerateDevices();
                    const updatedAudioOutputs = devices.filter(device => device.kind === 'audiooutput');
                    
                    // Если появилось больше устройств, проверяем их
                    if (updatedAudioOutputs.length > audioOutputs.length) {
                        return this.checkForExternalHeadphones(updatedAudioOutputs);
                    }
                    
                    // Используем обновленный список
                    return this.checkForExternalHeadphones(updatedAudioOutputs);
                } catch (error) {
                    // Если пользователь не дал разрешение, блокируем микрофон (безопаснее)
                    console.warn('Разрешение на микрофон не получено для проверки наушников');
                    return false;
                }
            }
            
            return this.checkForExternalHeadphones(audioOutputs);
        } catch (error) {
            console.warn('Ошибка при проверке наушников:', error);
            // В случае ошибки блокируем микрофон на мобильных (безопаснее)
            return false;
        }
    }
    
    // Проверяет наличие внешних наушников в списке устройств
    checkForExternalHeadphones(audioOutputs) {
        // Ключевые слова для внешних наушников
        const headphoneKeywords = [
            'headphone', 'headset', 'earphone', 'earbud', 'earbuds', 'earphones',
            'bluetooth', 'bt', 'wireless', 'wired',
            'наушник', 'гарнитур', 'bluetooth',
            'airpods', 'galaxy buds', 'pixel buds', 'jbl', 'sony', 'bose',
            'beats', 'sennheiser', 'audio-technica', 'akg', 'shure', 'jabra',
            'skullcandy', 'razer', 'logitech', 'hyperx', 'steelseries'
        ];
        
        // Ключевые слова для встроенных динамиков (их нужно исключить)
        const builtInSpeakerKeywords = [
            'speaker', 'динамик', 'built-in', 'internal', 'default',
            'встроенный', 'внутренний', 'receiver', 'earpiece'
        ];
        
        let hasExternalHeadphones = false;
        let hasOnlyBuiltInSpeakers = true;
        
        for (const device of audioOutputs) {
            const deviceLabel = (device.label || '').toLowerCase();
            
            // Пропускаем устройства без названия
            if (!deviceLabel || deviceLabel.trim() === '') {
                continue;
            }
            
            // Проверяем, является ли устройство встроенным динамиком
            let isBuiltInSpeaker = false;
            for (const keyword of builtInSpeakerKeywords) {
                if (deviceLabel.includes(keyword)) {
                    isBuiltInSpeaker = true;
                    break;
                }
            }
            
            // Если устройство не является встроенным динамиком
            if (!isBuiltInSpeaker) {
                hasOnlyBuiltInSpeakers = false;
                
                // Проверяем ключевые слова наушников
                for (const keyword of headphoneKeywords) {
                    if (deviceLabel.includes(keyword)) {
                        hasExternalHeadphones = true;
                        break;
                    }
                }
                
                // Если устройство не является встроенным динамиком и имеет название,
                // и это не просто "default", считаем его внешним устройством
                if (deviceLabel !== 'default' && deviceLabel.trim() !== '') {
                    hasExternalHeadphones = true;
                }
            }
        }
        
        // Если есть внешние наушники, разрешаем микрофон
        if (hasExternalHeadphones) {
            return true;
        }
        
        // Если есть только встроенные динамики, блокируем микрофон
        if (hasOnlyBuiltInSpeakers) {
            return false;
        }
        
        // Если устройств несколько и среди них есть не встроенные, разрешаем
        if (audioOutputs.length > 1) {
            return true;
        }
        
        // По умолчанию блокируем (безопаснее)
        return false;
    }
    
    // Обновляет состояние кнопки микрофона в зависимости от наличия наушников
    async updateMicrophoneButtonState() {
        const micBtn = document.getElementById('micBtn');
        if (!micBtn) return;
        
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
        
        // На мобильных устройствах ВСЕГДА блокируем микрофон
        if (isMobile) {
            micBtn.disabled = true;
            micBtn.title = translate('Микрофон недоступен на мобильных устройствах');
            micBtn.style.opacity = '0.5';
            micBtn.style.cursor = 'not-allowed';
        } else {
            // На десктопе разрешаем использование микрофона
            micBtn.disabled = false;
            micBtn.title = translate('Включить микрофон');
            micBtn.style.opacity = '1';
            micBtn.style.cursor = 'pointer';
        }
    }

    async handlePlayAll() {
        // Устанавливаем флаг, что выполняется handlePlayAll
        this.isPlayingPlayAll = true;
        
        try {
            // Инициализируем Tone.js и ждем полной готовности
            const toneReady = await this.initializeTone();
            if (!toneReady) {
                throw new Error(translate('Не удалось инициализировать Tone.js'));
            }

            // Убеждаемся, что Tone.js действительно запущен
            if (Tone.getContext().state !== 'running') {
                await Tone.start();
            }
            
            // Проверяем, загружена ли последовательность (она должна быть загружена при инициализации)
            let sequence = getFullMelodySequence();
            
            // Если последовательность не загружена, загружаем данные
            if (!sequence || sequence.length === 0) {
                await initNotes();
                sequence = getFullMelodySequence();
                
                // Если после загрузки последовательность все еще пуста, выдаем ошибку
                if (!sequence || sequence.length === 0) {
                    throw new Error(translate('Не удалось загрузить последовательность. Убедитесь, что данные доступны.'));
                }
            }
            
            // Подсчитываем общее количество нот в последовательности для отслеживания точности
            this.playAllStats.totalNotes = countTotalNotesInSequence();
            this.playAllStats.correctCount = 0;
            this.playAllStats.trackedNotes.clear();
            
            // Получаем songId для формирования пути
            const userConfig = await getUserConfig();
            const userProgress = userConfig.userProgress || [];
            const songId = userProgress[0]?.song_id || 1;
            
            // Устанавливаем URL ДО загрузки
            await setUrl(getSongBackgroundAudioPath(songId));
            
            // Показываем экран загрузки с прогрессом и ждем завершения загрузки
            let loadingComplete = false;
            await this.showLoadingScreen(translate('Загрузка музыки...'), async (updateProgress, finishLoading) => {
                try {
                    // Загружаем музыку с отслеживанием прогресса (без запуска воспроизведения)
                    await loadMusicWithProgress(updateProgress);
                    loadingComplete = true;
                    finishLoading();
                } catch (error) {
                    console.error('Ошибка загрузки музыки:', error);
                    loadingComplete = true;
                    finishLoading();
                }
            });
            
            // Убеждаемся, что загрузка завершена
            if (!loadingComplete) {
                throw new Error(translate('Загрузка музыки не завершена'));
            }
            
            // Инициализируем тюнер асинхронно (не блокируем воспроизведение)
            initTuner(true).catch(err => console.error('Ошибка инициализации тюнера:', err));
            
            stopGame();
            
            // Небольшая задержка для стабилизации перед обратным отсчетом
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Подготавливаем и отображаем ноты первого блока во время отсчета
            prepareFirstBlockNotesForCountdown();
            
            // Показываем обратный отсчет
            try {
                await startCountdown();
            } catch (error) {
                // Если countdown был остановлен, прерываем запуск
                if (error.message === 'Countdown stopped') {
                    return;
                }
                throw error;
            }
            
            // Запускаем отслеживание попаданий для режима "проиграть все"
            this.startPlayAllTracking();
            
            // Инициализируем и показываем кардиограф для режима playAll
            updateGameUI();
            
            // После обратного отсчета запускаем воспроизведение (файл уже загружен)
            await playMelody();
            
            // После окончания воспроизведения собираем статистику и показываем результаты
            await this.stopPlayAllTrackingAndShowResults();
        } catch (error) {
            this.handleError(translate('Ошибка при воспроизведении всей последовательности:'), error);
            // Останавливаем отслеживание в случае ошибки
            this.stopPlayAllTracking();
        } finally {
            // Сбрасываем флаг после завершения (в том числе при ошибках)
            this.isPlayingPlayAll = false;
        }
    }

    // Запуск отслеживания попаданий для режима "проиграть все"
    startPlayAllTracking() {
        if (!threeJSBand) {
            return;
        }

        this.playAllStats.isTracking = true;
        this.playAllStats.correctCount = 0;
        this.playAllStats.trackedNotes.clear();

        // Сохраняем время начала отслеживания для определения нот, созданных во время воспроизведения
        this.playAllStats.startTrackingTime = Tone.now();

        // Устанавливаем глобальный флаг для режима "проиграть все"
        window.isPlayAllMode = true;

        // Периодически проверяем попадания по нотам
        const checkHits = () => {
            if (!this.playAllStats.isTracking || !threeJSBand) {
                return;
            }

            // Проверяем все ноты, которые достигли грифа
            // В режиме "проиграть все" все ноты имеют isGameBlockNote: true, поэтому убираем эту проверку
            let anyNewHit = false;
            threeJSBand.movingNotes.forEach((note, index) => {
                if (note.reachedFretboard && note.noteData && note.noteData.frequency) {
                    // Создаем уникальный идентификатор ноты (string-fret-targetTime)
                    // Используем originalTargetTime для уникальности, так как targetTime может изменяться
                    const noteId = `${note.string}-${note.fret}-${note.originalTargetTime || note.targetTime || note.sequenceIndex || index}`;
                    
                    // Если нота попала и еще не была учтена
                    if (note.hit && !this.playAllStats.trackedNotes.has(noteId)) {
                        this.playAllStats.trackedNotes.add(noteId);
                        anyNewHit = true;
                    }
                }
            });

            // Обновляем счетчик правильных попаданий на основе размера trackedNotes
            this.playAllStats.correctCount = this.playAllStats.trackedNotes.size;
            
            // Запускаем анимацию "удара сердца" если было новое попадание
            if (anyNewHit) {
                triggerHeartbeatJump();
            }

            // Продолжаем отслеживание
            this.playAllStats.trackingInterval = setTimeout(checkHits, 100); // Проверяем каждые 100мс
        };

        checkHits();
    }

    // Остановка отслеживания и показ результатов
    async stopPlayAllTrackingAndShowResults() {
        // Останавливаем отслеживание
        this.stopPlayAllTracking();

        // Даем немного времени для завершения всех обработок попаданий
        await new Promise(resolve => setTimeout(resolve, 500));

        // Собираем финальную статистику из всех нот
        if (threeJSBand && threeJSBand.movingNotes) {
            // Проверяем все ноты еще раз на случай, если были последние попадания
            threeJSBand.movingNotes.forEach((note, index) => {
                if (note.reachedFretboard && note.noteData && note.noteData.frequency) {
                    // Создаем уникальный идентификатор ноты (string-fret-targetTime)
                    const noteId = `${note.string}-${note.fret}-${note.originalTargetTime || note.targetTime || note.sequenceIndex || index}`;
                    
                    if (note.hit && !this.playAllStats.trackedNotes.has(noteId)) {
                        this.playAllStats.trackedNotes.add(noteId);
                    }
                }
            });
        }

        // Обновляем счетчик правильных попаданий на основе размера trackedNotes
        this.playAllStats.correctCount = this.playAllStats.trackedNotes.size;

        // Вычисляем точность
        const totalNotes = this.playAllStats.totalNotes || 0;
        const correctCount = this.playAllStats.correctCount || 0;
        const accuracy = totalNotes > 0 
            ? Math.min(100, Math.round((correctCount / totalNotes) * 100))
            : 0;

        // Сохраняем статистику полной последовательности всегда, если была попытка игры
        // (даже если тюнер не был активен - это все равно попытка)
        if (totalNotes > 0) {
            try {
                const userConfig = await getUserConfig();
                const userProgress = userConfig.userProgress || [];
                const songId = userProgress[0]?.song_id || 1;
                const userId = auth.getUserId();
                
                if (userId && songId) {
                    const { saveFullSequenceStats } = await import('./api.js');
                    await saveFullSequenceStats({
                        user_id: userId,
                        song_id: songId,
                        accuracy: accuracy,
                        total_notes: totalNotes,
                        correct_notes: correctCount
                    });
                }
            } catch (error) {
                console.error('Ошибка при сохранении статистики полной последовательности:', error);
                errorLogger.logError({
                    error_type: 'save_full_sequence_stats_error',
                    message: `Ошибка при сохранении статистики полной последовательности: ${error.message}`,
                    stack: error.stack,
                    url: window.location.href
                });
            }
        }
        
        // Показываем сообщение с результатами только если:
        // 1. Была хотя бы одна попытка игры (correctCount > 0)
        // 2. Тюнер был активен (микрофон был подключен и работал)
        // Если пользователь не подключал микрофон, не показываем результаты
        const tunerWasActive = isTunerActive();
        if (totalNotes > 0 && correctCount > 0 && tunerWasActive) {
            await showPlayAllResultMessage(accuracy, correctCount, totalNotes);
        }
    }

    // Остановка отслеживания
    stopPlayAllTracking() {
        this.playAllStats.isTracking = false;
        window.isPlayAllMode = false; // Сбрасываем флаг режима "проиграть все"
        if (this.playAllStats.trackingInterval) {
            clearTimeout(this.playAllStats.trackingInterval);
            this.playAllStats.trackingInterval = null;
        }
        // Обновляем UI чтобы скрыть кардиограф
        updateGameUI();
    }

    updateSpeakerIcon(isPlaying) {
        const speakerBtn = document.getElementById('startPlayMusic');
        if (speakerBtn) {
            if (isPlaying) {
                speakerBtn.classList.add('playing');
            } else {
                speakerBtn.classList.remove('playing');
            }
        }
    }

    async handleStartPlayMusic() {
        try {
            // СНАЧАЛА проверяем, играет ли уже музыка
            const isCurrentlyPlaying = getIsPlaying();
            console.log('🎵 handleStartPlayMusic вызван, isPlaying:', isCurrentlyPlaying);
            
            if (isCurrentlyPlaying) {
                // Если играет, просто останавливаем и выходим
                console.log('🛑 Остановка воспроизведения мелодии');
                // Прерываем текущий запуск
                if (this.isStartingPlayMusic && this.startPlayMusicAbortController) {
                    this.startPlayMusicAbortController.abort();
                    this.isStartingPlayMusic = false;
                }
                await this.handleStopPlay();
                this.updateSpeakerIcon(false);
                return;
            }
            
            // Если уже выполняется запуск музыки, прерываем предыдущий
            if (this.isStartingPlayMusic && this.startPlayMusicAbortController) {
                console.log('⚠️ Прерывание предыдущего запуска музыки');
                this.startPlayMusicAbortController.abort();
            }
            
            // Создаем новый AbortController для этого запуска
            this.startPlayMusicAbortController = new AbortController();
            const signal = this.startPlayMusicAbortController.signal;
            this.isStartingPlayMusic = true;
            
            console.log('▶️ Запуск воспроизведения мелодии');

            // Останавливаем всё перед запуском новой музыки
            await this.handleStopPlay();
            
            // КРИТИЧНО: Проверяем прерывание после КАЖДОГО await
            if (signal.aborted) {
                console.log('⛔ Запуск прерван после handleStopPlay');
                this.isStartingPlayMusic = false;
                window.isListenMode = false;
                return;
            }
            const toneReady = await this.initializeTone();
            
            if (signal.aborted) {
                console.log('⛔ Запуск прерван после initializeTone');
                this.isStartingPlayMusic = false;
                return;
            }
            
            if (toneReady) {
                await initNotes();
                
                if (signal.aborted) {
                    console.log('⛔ Запуск прерван после initNotes');
                    this.isStartingPlayMusic = false;
                    window.isListenMode = false;
                    return;
                }
                
                stopGame();
                
                // Получаем songId для формирования пути
                const userConfig = await getUserConfig();
                
                if (signal.aborted) {
                    console.log('⛔ Запуск прерван после getUserConfig');
                    this.isStartingPlayMusic = false;
                    window.isListenMode = false;
                    return;
                }
                
                const userProgress = userConfig.userProgress || [];
                const songId = userProgress[0]?.song_id || 1;
                
                // Устанавливаем URL
                await setUrl(getSongFullAudioPath(songId));
                
                if (signal.aborted) {
                    console.log('⛔ Запуск прерван после setUrl');
                    this.isStartingPlayMusic = false;
                    window.isListenMode = false;
                    return;
                }
                
                // Показываем экран загрузки с прогрессом и ждем завершения загрузки
                let loadingComplete = false;
                await this.showLoadingScreen('Загрузка музыки...', async (updateProgress, finishLoading) => {
                    try {
                        // Загружаем музыку с отслеживанием прогресса (без запуска воспроизведения)
                        await loadMusicWithProgress(updateProgress);
                        loadingComplete = true;
                        finishLoading();
                } catch (error) {
                    console.error(translate('Ошибка загрузки музыки:'), error);
                    loadingComplete = true;
                    finishLoading();
                }
            });
            
            // Убеждаемся, что загрузка завершена
            if (!loadingComplete) {
                throw new Error(translate('Загрузка музыки не завершена'));
            }
            
            if (signal.aborted) {
                console.log('⛔ Запуск прерван после загрузки музыки');
                this.isStartingPlayMusic = false;
                window.isListenMode = false;
                return;
            }
                
                // Небольшая задержка перед обратным отсчетом
                await new Promise(resolve => setTimeout(resolve, 300));
                
                if (signal.aborted) {
                    console.log('⛔ Запуск прерван после задержки');
                    this.isStartingPlayMusic = false;
                    window.isListenMode = false;
                    return;
                }
                
                // Подготавливаем и отображаем ноты первого блока во время отсчета
                prepareFirstBlockNotesForCountdown();
                
            // Устанавливаем флаг режима "прослушать песню"
            window.isListenMode = true;
            
            try {
                // Показываем обратный отсчет
                try {
                    await startCountdown();
                    
                    if (signal.aborted) {
                        console.log('⛔ Запуск прерван после countdown');
                        this.isStartingPlayMusic = false;
                        window.isListenMode = false;
                        return;
                    }
                } catch (error) {
                    // Если countdown был остановлен, прерываем запуск
                    if (error.message === 'Countdown stopped') {
                        console.log('⛔ Countdown остановлен');
                        return;
                    }
                    throw error;
                }
                
                // После обратного отсчета запускаем воспроизведение (файл уже загружен)
                this.updateSpeakerIcon(true);
                await playMelody();
            } finally {
                // Сбрасываем флаг после завершения (в том числе при ошибках)
                window.isListenMode = false;
                this.updateSpeakerIcon(false);
                this.isStartingPlayMusic = false;
            }
            }
        } catch (error) {
            // Игнорируем ошибку ручной остановки и прерывания - это нормальное поведение
            const isInterrupted = error.message === 'Playback stopped manually' || 
                                 error.name === 'AbortError' ||
                                 (this.startPlayMusicAbortController && this.startPlayMusicAbortController.signal.aborted);
            
            if (!isInterrupted) {
                this.handleError(translate('Ошибка при воспроизведении трека:'), error);
            }
            this.updateSpeakerIcon(false);
            this.isStartingPlayMusic = false;
            
            // Убеждаемся, что все overlay удалены при ошибке
            const loadingOverlay = document.querySelector('.loading-overlay');
            if (loadingOverlay && loadingOverlay.parentNode) {
                loadingOverlay.parentNode.removeChild(loadingOverlay);
            }
            
            // Останавливаем countdown, если он активен
            try {
                const { stopCountdown } = await import('./utils/countdown.js');
                stopCountdown();
            } catch (e) {
                // Игнорируем ошибки импорта
            }
        }
    }

    async showLoadingScreen(message = 'Загрузка...', progressCallback = null) {
        return new Promise((resolve) => {
            const loadingContainer = this.createLoadingContainer();
            const messageElement = document.createElement('div');
            messageElement.className = 'loading-message';
            messageElement.textContent = message;
            messageElement.style.cssText = `
                font-size: 48px;
                margin-bottom: 40px;
                color: white;
                text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
            `;
            
            const progressContainer = document.createElement('div');
            progressContainer.className = 'loading-progress';
            progressContainer.style.cssText = `
                width: 500px;
                height: 40px;
                background: rgba(255, 255, 255, 0.15);
                border-radius: 20px;
                overflow: hidden;
                margin-bottom: 25px;
                border: 2px solid rgba(255, 255, 255, 0.3);
                box-shadow: 0 4px 15px rgba(0,0,0,0.3);
            `;
            
            const progressBar = document.createElement('div');
            progressBar.style.cssText = `
                width: 0%;
                height: 100%;
                background: linear-gradient(90deg, #4CAF50, #8BC34A, #CDDC39);
                transition: width 0.2s ease;
                border-radius: 20px;
                box-shadow: 0 0 20px rgba(76, 175, 80, 0.5);
            `;
            progressContainer.appendChild(progressBar);
            
            const progressText = document.createElement('div');
            progressText.className = 'loading-progress-text';
            progressText.textContent = '0%';
            progressText.style.cssText = `
                font-size: 32px;
                color: white;
                font-weight: bold;
                text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
            `;
            
            loadingContainer.appendChild(messageElement);
            loadingContainer.appendChild(progressContainer);
            loadingContainer.appendChild(progressText);
            document.body.appendChild(loadingContainer);

            // Если передан callback для обновления прогресса
            if (progressCallback) {
                progressCallback((progress) => {
                    const percent = Math.min(100, Math.max(0, progress));
                    progressBar.style.width = percent + '%';
                    progressText.textContent = Math.round(percent) + '%';
                    
                    // Добавляем эффект пульсации при достижении 100%
                    if (percent >= 100) {
                        progressBar.style.boxShadow = '0 0 30px rgba(76, 175, 80, 0.8)';
                        progressText.textContent = translate('Готово!');
                    }
                }, () => {
                    // Callback для завершения загрузки
                    setTimeout(() => {
                        if (loadingContainer.parentNode) {
                            loadingContainer.parentNode.removeChild(loadingContainer);
                        }
                        resolve();
                    }, 500);
                });
            } else {
                // Без callback просто ждем немного и закрываем
                setTimeout(() => {
                    if (loadingContainer.parentNode) {
                        loadingContainer.parentNode.removeChild(loadingContainer);
                    }
                    resolve();
                }, 500);
            }
        });
    }

    createLoadingContainer() {
        const container = document.createElement('div');
        container.className = 'loading-overlay';
        container.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.9);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            z-index: 10000;
            font-size: 120px;
            color: white;
            font-weight: bold;
        `;
        return container;
    }

    async handleStopPlay() {
        // Если уже выполняется остановка, возвращаем существующий Promise
        if (this.isStopping && this.stopPlayPromise) {
            return this.stopPlayPromise;
        }

        // Устанавливаем флаг и создаем Promise для остановки
        this.isStopping = true;
        this.stopPlayPromise = this._performStopPlay();

        setupBlockClickHandlers();

        try {
            await this.stopPlayPromise;
        } finally {
            this.isStopping = false;
            this.stopPlayPromise = null;
        }
    }

    async _performStopPlay() {
        try {
            // ПРЕЖДЕ ВСЕГО: прерываем текущий процесс запуска блока через AbortController
            // Это нужно сделать ДО всех остальных действий, чтобы startGameFromBlock
            // мог проверить abortSignal.aborted на всех критических точках
            if (window.gameManager && window.gameManager.startBlockAbortController) {
                window.gameManager.startBlockAbortController.abort();
            }

            // Сбрасываем флаг режима "проиграть все" при остановке
            // Это важно, чтобы синтезатор и фоновая музыка могли запуститься при следующем запуске блока
            window.isPlayAllMode = false;
            if (this.playAllStats) {
                this.playAllStats.isTracking = false;
            }

            await stopGame();

            // СНАЧАЛА полностью очищаем все ноты и останавливаем анимации
            // Это предотвращает создание новых нот во время остановки
            if (window.threeJSBand) {
                window.threeJSBand.fullReset();
            }
            
            // Останавливаем воспроизведение последовательности и фоновой музыки
            await stopPlayback();
            
            // Останавливаем воспроизведение блоков
            await stopBlockPlayback();
            
            // Повторно очищаем ноты на случай, если что-то успело создать их заново
            if (window.threeJSBand) {
                window.threeJSBand.fullReset();
            }
            
            // Снимаем подсветку со всех блоков мелодии (.melody-block .playing)
            document.querySelectorAll('.melody-block').forEach(block => {
                block.classList.remove('playing');
                block.classList.remove('active');
            });
            
            // Снимаем подсветку со всех блоков последовательности (.sequence-block .active)
            document.querySelectorAll('.sequence-block').forEach(block => {
                block.classList.remove('active');
            });
            
            // Обновляем иконку динамика
            this.updateSpeakerIcon(false);
        } catch (error) {
            console.error('Ошибка при остановке воспроизведения:', error);
            // Все равно обновляем иконку и убираем подсветки даже при ошибке
            this.updateSpeakerIcon(false);
            document.querySelectorAll('.melody-block, .sequence-block').forEach(block => {
                block.classList.remove('playing', 'active');
            });
            if (window.threeJSBand) {
                window.threeJSBand.fullReset();
            }
            // Сбрасываем флаг даже при ошибке
            window.isPlayAllMode = false;
            if (this.playAllStats) {
                this.playAllStats.isTracking = false;
            }
        }
    }

    async handleResetProgress() {
        if (confirm(getConfirmMessage('RESET_PROGRESS'))) {
            await resetGameProgress();
            await this.restartGame();
        }
    }

    async restartGame() {
        await initNotes();
        const data = getSongData();
        if (data) {
            initGame(data);
        }
    }

    async initializeTone() {
        try {
            // Используем наш AudioContextManager
            const audioContext = window.audioContextManager.getAudioContext();

            // Синхронизируем Tone.js с нашим AudioContext
            if (Tone.context !== audioContext) {
                Tone.setContext(audioContext);
            }

            if (Tone.getContext().state !== 'running') {
                await Tone.start();
            }
            return true;
        } catch (error) {
            this.handleError('Ошибка инициализации Tone.js:', error);
            return false;
        }
    }

    toggleTab() {
        // Переключаем режим
        this.currentTabMode = this.currentTabMode === 'guitar' ? 'settings' : 'guitar';
        this.switchTab(this.currentTabMode);
    }

    switchTab(tabName) {
        this.currentTabMode = tabName;
        const toggleBtn = this.elements.toggleTabBtn;
        if (toggleBtn) {
            toggleBtn.dataset.tab = tabName;
            toggleBtn.classList.add('active');
        }
        this.showActiveTab();
        this.updateTabIcon();
    }

    updateTabIcon() {
        const toggleBtn = this.elements.toggleTabBtn;
        if (!toggleBtn) return;

        const guitarIcon = toggleBtn.querySelector('.guitar-icon');
        const settingsIcon = toggleBtn.querySelector('.settings-icon');

        if (this.currentTabMode === 'guitar') {
            // Показываем иконку шестеренки для режима игры, скрываем гитару
            if (guitarIcon) guitarIcon.style.display = 'none';
            if (settingsIcon) settingsIcon.style.display = 'block';
        } else {
            // Показываем иконку гитары для режима настроек, скрываем шестеренку
            if (guitarIcon) guitarIcon.style.display = 'block';
            if (settingsIcon) settingsIcon.style.display = 'none';
        }
    }

    showActiveTab() {
        const guitarSection = document.querySelector(SELECTORS.GUITAR_SECTION);
        const settingsSection = document.querySelector(SELECTORS.SETTINGS_SECTION);
        
        guitarSection?.classList.remove('active');
        settingsSection?.classList.remove('active');

        if (this.currentTabMode === 'guitar') {
            guitarSection?.classList.add('active');
        } else {
            settingsSection?.classList.add('active');
        }
    }

    handleError(message, error) {
        console.error(message, error);
        alert(`${translate('Произошла ошибка:')} ${error.message}`);
    }

    // Обработка клика на имя пользователя
    handleUserNameClick() {
        const isGuest = auth.isGuestUser();
        
        if (isGuest) {
            // Гостевой пользователь - показываем окно авторизации
            this.showAuthModal();
        } else {
            // Авторизованный пользователь - показываем окно редактирования профиля
            this.showProfileEditModal();
        }
    }

    // Показать модальное окно авторизации
    showAuthModal() {
        const authModal = document.getElementById('authModal');
        if (authModal) {
            authModal.style.display = 'flex';
            // Показываем форму входа
            document.getElementById('loginForm').style.display = 'block';
            document.getElementById('registerForm').style.display = 'none';
            document.getElementById('authTitle').textContent = translate('Вход');
        }
    }

    // Показать модальное окно редактирования профиля
    showProfileEditModal() {
        // Создаем модальное окно если его нет
        let profileModal = document.getElementById('profileEditModal');
        if (!profileModal) {
            profileModal = this.createProfileEditModal();
        }
        
        // Заполняем текущие данные
        const usernameField = profileModal.querySelector('#profileUsername');
        if (usernameField) {
            usernameField.value = auth.getUsername();
        }
        
        // Проверяем: это гостевой аккаунт из БД?
        const isGuest = auth.currentUser && 
                       auth.currentUser.name && 
                       auth.currentUser.name.startsWith('guest_');
        
        // Для гостя: скрываем поле текущего пароля и показываем подсказку
        const currentPasswordGroup = profileModal.querySelector('#profileCurrentPassword')?.parentElement;
        const errorEl = profileModal.querySelector('#profileError');
        
        if (isGuest) {
            if (currentPasswordGroup) {
                currentPasswordGroup.style.display = 'none';
            }
            if (errorEl) {
                errorEl.textContent = translate('Вы прошли уровень! Придумайте новое имя и пароль для сохранения прогресса.');
                errorEl.style.display = 'block';
                errorEl.style.color = '#4caf50'; // Зеленый цвет для подсказки
            }
            // Меняем метку для нового пароля - делаем его обязательным
            const newPasswordLabel = profileModal.querySelector('label[for="profileNewPassword"]');
            if (newPasswordLabel) {
                newPasswordLabel.textContent = translate('Новый пароль');
            }
        } else {
            if (currentPasswordGroup) {
                currentPasswordGroup.style.display = 'block';
            }
            if (errorEl) {
                errorEl.style.display = 'none';
            }
        }
        
        // Показываем модальное окно
        profileModal.style.display = 'flex';
    }

    // Создать модальное окно редактирования профиля
    createProfileEditModal() {
        const modal = document.createElement('div');
        modal.id = 'profileEditModal';
        modal.className = 'modal';
        modal.innerHTML = `
            <div class="modal-content">
                <h2>${translate('Редактирование профиля')}</h2>
                <div class="form-group">
                    <label for="profileUsername">${translate('Имя пользователя')}</label>
                    <input type="text" id="profileUsername" placeholder="${translate('Введите новое имя')}" />
                </div>
                <div class="form-group">
                    <label for="profileCurrentPassword">${translate('Текущий пароль')}</label>
                    <input type="password" id="profileCurrentPassword" placeholder="${translate('Для подтверждения изменений')}" />
                </div>
                <div class="form-group">
                    <label for="profileNewPassword">${translate('Новый пароль (необязательно)')}</label>
                    <input type="password" id="profileNewPassword" placeholder="${translate('Оставьте пустым если не хотите менять')}" />
                </div>
                <div class="form-group">
                    <label for="profileNewPasswordConfirm">${translate('Подтвердите новый пароль')}</label>
                    <input type="password" id="profileNewPasswordConfirm" placeholder="${translate('Повторите новый пароль')}" />
                </div>
                <div id="profileError" class="error-message"></div>
                <div class="button-group">
                    <button id="profileSaveBtn" class="btn primary">${translate('Сохранить')}</button>
                    <button id="profileCancelBtn" class="btn secondary">${translate('Отмена')}</button>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        
        // Обработчики кнопок
        modal.querySelector('#profileSaveBtn').addEventListener('click', () => this.handleProfileSave());
        modal.querySelector('#profileCancelBtn').addEventListener('click', () => this.closeProfileEditModal());
        
        // Закрытие по клику вне модального окна
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                this.closeProfileEditModal();
            }
        });
        
        return modal;
    }

    // Закрыть модальное окно редактирования профиля
    closeProfileEditModal() {
        const modal = document.getElementById('profileEditModal');
        if (modal) {
            modal.style.display = 'none';
            // Очищаем поля
            modal.querySelector('#profileCurrentPassword').value = '';
            modal.querySelector('#profileNewPassword').value = '';
            modal.querySelector('#profileNewPasswordConfirm').value = '';
            // Скрываем ошибки
            const errorEl = modal.querySelector('#profileError');
            if (errorEl) {
                errorEl.style.display = 'none';
            }
        }
    }

    // Сохранить изменения профиля
    async handleProfileSave() {
        const modal = document.getElementById('profileEditModal');
        const username = modal.querySelector('#profileUsername').value.trim();
        const currentPassword = modal.querySelector('#profileCurrentPassword').value;
        const newPassword = modal.querySelector('#profileNewPassword').value;
        const newPasswordConfirm = modal.querySelector('#profileNewPasswordConfirm').value;
        const errorEl = modal.querySelector('#profileError');
        
        // Проверяем: это гостевой аккаунт?
        const isGuest = auth.currentUser && 
                       auth.currentUser.name && 
                       auth.currentUser.name.startsWith('guest_');
        
        // Валидация
        if (!username) {
            this.showProfileError(translate('Введите имя пользователя'));
            return;
        }
        
        // Для гостя: не требуем текущий пароль, но требуем новый
        if (isGuest) {
            if (!newPassword) {
                this.showProfileError(translate('Для гостевого аккаунта необходимо задать новый пароль'));
                return;
            }
            if (newPassword.length < 6) {
                this.showProfileError(translate('Пароль должен быть не менее 6 символов'));
                return;
            }
        } else {
            // Для обычного пользователя: требуем текущий пароль
            if (!currentPassword) {
                this.showProfileError(translate('Введите текущий пароль для подтверждения'));
                return;
            }
        }
        
        if (newPassword && newPassword !== newPasswordConfirm) {
            this.showProfileError(translate('Новые пароли не совпадают'));
            return;
        }
        
        // Отправляем запрос на обновление
        const saveBtn = modal.querySelector('#profileSaveBtn');
        saveBtn.disabled = true;
        saveBtn.textContent = translate('Сохранение...');
        
        const requestData = {
            user_id: auth.currentUser.id,
            username: username,
            current_password: isGuest ? '' : currentPassword,
            new_password: newPassword || null
        };
        
        console.log('Sending update request:', { 
            isGuest, 
            currentUserName: auth.currentUser.name,
            ...requestData, 
            current_password: requestData.current_password ? '***' : '(empty)',
            new_password: requestData.new_password ? '***' : '(empty)'
        });
        
        try {
            const response = await fetch('/api/user/update', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });
            
            console.log('Response status:', response.status, response.ok);
            const result = await response.json();
            console.log('Profile update result:', result);
            
            if (response.ok && result.success) {
                // Обновляем локальные данные
                if (auth.currentUser) {
                    auth.currentUser.name = username;
                    auth.saveToStorage();
                }
                
                // Обновляем отображение
                await this.updateUserAndSongInfo();
                
                // Закрываем модальное окно
                this.closeProfileEditModal();
                
                alert(translate('Профиль успешно обновлен!'));
            } else {
                console.error('Profile update failed:', result);
                this.showProfileError(result.error || translate('Ошибка при обновлении профиля'));
            }
        } catch (error) {
            console.error('Ошибка при обновлении профиля:', error);
            this.showProfileError(translate('Ошибка соединения с сервером'));
        } finally {
            saveBtn.disabled = false;
            saveBtn.textContent = translate('Сохранить');
        }
    }

    // Показать ошибку в модальном окне профиля
    showProfileError(message) {
        const modal = document.getElementById('profileEditModal');
        const errorEl = modal?.querySelector('#profileError');
        if (errorEl) {
            errorEl.textContent = message;
            errorEl.style.display = 'block';
            errorEl.style.color = '#ff5252';
        }
    }
}

const app = new App();
// Делаем app и threeJSBand доступными глобально для обработчика попаданий
window.app = app;

// Инициализация Telegram Mini App - разворачиваем на весь экран при загрузке
function initTelegramMiniApp() {
    // Проверяем, что мы находимся в Telegram WebApp
    if (typeof window.Telegram !== 'undefined' && window.Telegram.WebApp) {
        const tg = window.Telegram.WebApp;
        // Уведомляем Telegram, что приложение готово
        tg.ready();
        // Вызываем requestFullscreen только если метод существует (т.е. мы точно в Telegram)
        if (tg.requestFullscreen && typeof tg.requestFullscreen === 'function') {
            try {
                tg.requestFullscreen();
            } catch (error) {
                console.warn('Failed to request fullscreen in Telegram:', error);
            }
        }
        // Добавляем класс для CSS стилей
        document.body.classList.add('telegram');
    }
}

// Функция для адаптивного масштабирования на мобильных устройствах
function setupMobileScaling() {
    const container = document.getElementById('mainContainer');
    if (!container) {
        // Если контейнер еще не создан, попробуем позже
        setTimeout(setupMobileScaling, 100);
        return;
    }
    
    const updateScale = () => {
        // Проверяем, виден ли контейнер
        if (container.style.display === 'none') {
            return;
        }
        
        // Проверяем, запущено ли в Telegram
        const isTelegram = typeof window.Telegram !== 'undefined' && window.Telegram.WebApp;
        
        // Если в Telegram - не применяем масштабирование, Telegram сам разворачивает
        if (isTelegram) {
            container.style.transform = '';
            container.style.width = '';
            container.style.height = '';
            // Для Telegram все равно применяем масштабирование к модальным окнам
            updateModalScaling(isTelegram);
            return;
        }
        
        // Для мобильных устройств (не в Telegram) - НЕ применяем масштабирование
        // Используем нативную мобильную верстку через CSS медиа-запросы
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
        
        // Убираем любое масштабирование для нативной мобильной верстки
        container.style.transform = '';
        container.style.width = '';
        container.style.height = '';
        updateModalScaling(false);
    };
    
    // Функция для масштабирования модальных окон
    const updateModalScaling = (isTelegram, scale = null) => {
        // Для мобильных устройств (не Telegram) не применяем масштабирование
        // Используем нативную мобильную верстку через CSS
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
        
        if (isMobile && !isTelegram) {
            // На мобильных не масштабируем, используем CSS
            scale = 1;
        } else if (scale === null) {
            // На десктопе или при отсутствии scale
            scale = 1;
        }
        
        // Применяем масштаб к контенту модальных окон (overlay элементы не масштабируем)
        const modalSelectors = [
            '.auth-container',
            '.modal-content'
        ];
        
        modalSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                if (element) {
                    element.style.transform = `scale(${scale})`;
                    element.style.transformOrigin = 'center';
                }
            });
        });
        
        // Масштабируем контент внутри loading-overlay
        const loadingContentSelectors = [
            '.loading-message',
            '.loading-progress',
            '.loading-progress-text'
        ];
        
        loadingContentSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                if (element) {
                    element.style.transform = `scale(${scale})`;
                    element.style.transformOrigin = 'center';
                }
            });
        });
        
        // Масштабируем контент внутри игровых сообщений (overlay элементы не масштабируем)
        const gameMessageContentSelectors = [
            '.game-message-content'
        ];
        
        gameMessageContentSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                if (element) {
                    element.style.transform = `scale(${scale})`;
                    element.style.transformOrigin = 'center';
                }
            });
        });
        
        // Для tutorial-popup комбинируем масштабирование с центрированием
        const tutorialPopups = document.querySelectorAll('.tutorial-popup');
        tutorialPopups.forEach(element => {
            if (element) {
                element.style.transform = `translate(-50%, -50%) scale(${scale})`;
                element.style.transformOrigin = 'center';
            }
        });
    };
    
    // Вызываем сразу и после небольшой задержки (на случай, если контейнер еще не виден)
    updateScale();
    setTimeout(updateScale, 100);
    setTimeout(updateScale, 500);
    
    window.addEventListener('resize', updateScale);
    window.addEventListener('orientationchange', () => {
        setTimeout(updateScale, 100);
    });
    
    // Наблюдаем за изменением видимости контейнера
    const observer = new MutationObserver(() => {
        updateScale();
    });
    
    observer.observe(container, {
        attributes: true,
        attributeFilter: ['style']
    });
    
    // Наблюдаем за появлением модальных окон и применяем масштабирование
    const modalObserver = new MutationObserver(() => {
        updateModalScaling(typeof window.Telegram !== 'undefined' && window.Telegram.WebApp);
    });
    
    modalObserver.observe(document.body, {
        childList: true,
        subtree: true
    });
}

// Вызываем сразу при загрузке скрипта (для DirectLink)
if (typeof window.Telegram !== 'undefined' && window.Telegram.WebApp) {
    // Добавляем класс для CSS стилей
    if (document.body) {
        document.body.classList.add('telegram');
    } else {
        document.addEventListener('DOMContentLoaded', () => {
            document.body.classList.add('telegram');
        });
    }
    initTelegramMiniApp();
}

window.addEventListener('DOMContentLoaded', () => {
    // Проверяем, запущено ли в Telegram
    const isTelegram = typeof window.Telegram !== 'undefined' && window.Telegram.WebApp;
    
    if (isTelegram) {
        // Добавляем класс для CSS стилей
        document.body.classList.add('telegram');
        // В Telegram: инициализируем Telegram Mini App для развертывания на полный экран
        initTelegramMiniApp();
    } else {
        // Не в Telegram: настраиваем масштабирование для мобильных устройств
        setupMobileScaling();
    }
    
    // Инициализируем приложение
    app.init();
    
    // Обработчик клика на баннер Telegram
    const telegramBanner = document.getElementById('telegramBanner');
    if (telegramBanner) {
        telegramBanner.addEventListener('click', () => {
            // Замените YOUR_TELEGRAM_CHANNEL на ваш Telegram канал/группу (например: guitarpulse)
            const telegramLink = 'https://t.me/GuitarPulse_game';
            window.open(telegramLink, '_blank');
        });
    }
    
    // Если в Telegram, повторно вызываем expandTelegramApp после инициализации
    if (isTelegram) {
        setTimeout(() => {
            if (window.app && typeof window.app.expandTelegramApp === 'function') {
                window.app.expandTelegramApp();
            }
        }, 800);
    }
});

// Экспортируем экземпляр для использования в других модулях
export default app;